/* eslint-disable max-lines-per-function */
import { log } from '@atlassian/mpac-utils';
import { Either } from 'funfix-core';
import { List } from 'immutable';
import { INFO_APPEARANCE, makeFlag, makeFlagAction, makeFlagDescription, SUCCESS_APPEARANCE } from '../../records/Flag';
import { foldInstallableAppIdentifier, toSPIInstallableAddonIdentifier } from '../../records/InstallableAppIdentifier';
import getSPI from '../../services/SPI';
import { AppInstallTask } from '../../telemetry/tasks';
import { trackAppInstallTelemetryTaskError } from '../../utils/app-installation';
import { navigateToProductUrl } from '../../utils/package-connect-utils';
import { addCaasErrorFlag, addFlag, removeFlag } from '../Flags';
import { NO_SIMULTANEOUS_INSTALLS_FLAG_KEY } from './Installation';
import { updateAllRequestStatusesPostInstall, updateInstalledAppsPostInstall } from './postInstallActions';
export const START_INSTALL_APP = 'INSTALLATION_START_INSTALL_APP';
export const FINISH_INSTALL_APP = 'INSTALLATION_FINISH_INSTALL_APP';
export const INSTALL_APP_ERROR = 'INSTALLATION_INSTALL_APP_ERROR';
const startInstallApp = _ref => {
  let {
    installableAppId
  } = _ref;
  return {
    type: START_INSTALL_APP,
    installableAppId
  };
};
const finishInstallApp = _ref2 => {
  let {
    context,
    installableAppId,
    suppressResultFlag,
    featureFlags,
    appDetails
  } = _ref2;
  return {
    type: FINISH_INSTALL_APP,
    context,
    installableAppId,
    suppressResultFlag,
    featureFlags,
    appDetails
  };
};
const installAppError = _ref3 => {
  let {
    context,
    error,
    installableAppId
  } = _ref3;
  return {
    type: INSTALL_APP_ERROR,
    context,
    error,
    installableAppId
  };
};
export const doInstallApp = _ref4 => {
  let {
    context,
    hostConfig,
    hostInfo,
    mpacBaseUrl,
    installableApp,
    installableAppId,
    relayState,
    suppressProgressFlag = false,
    suppressResultFlag = false,
    featureFlags,
    locale
  } = _ref4;
  return dispatch => {
    const appIdentifier = foldInstallableAppIdentifier(installableAppId)(key => key.key, url => url.url, ari => ari.appKey);
    const loadingFlagKey = "".concat(appIdentifier, "-app-install-in-progress");
    dispatch(startInstallApp({
      installableAppId
    }));
    if (!suppressProgressFlag) {
      dispatch(addFlag({
        flag: makeFlag({
          key: loadingFlagKey,
          appearance: INFO_APPEARANCE,
          autoDismiss: false,
          title: "Adding ".concat(installableApp.details.name, "..."),
          description: makeFlagDescription({
            primary: "We'll tell you when your app has been added."
          }),
          actions: List([makeFlagAction({
            callback: () => {
              dispatch(removeFlag(loadingFlagKey));
            },
            displayText: 'Close',
            id: "".concat(appIdentifier, "-close")
          })])
        })
      }));
    }
    AppInstallTask.start();
    return getSPI()
    /**
     * The signature of installAddon has been changed. We will not be making a
     * change here since we are replacing this flow with the standalone app installation
     * flow
     */.installAddon({
      addon: toSPIInstallableAddonIdentifier(installableAppId),
      contextId: context.id,
      relayState
    }).then(installedAddon => {
      const key = "".concat(appIdentifier, "-app-install-success");
      const actions = List([{
        displayText: 'Get started',
        url: installedAddon.getStartedUrl,
        key: 'getStarted'
      }, {
        displayText: 'Configure',
        url: installedAddon.configureUrl,
        removeIfActionCountAbove: 2,
        key: 'config'
      }, {
        displayText: 'Manage app',
        url: installedAddon.manageAppUrl,
        key: 'manageApp'
      }]).filter(action => !!action.url).reduce((newList, action, _, currentList) => action.removeIfActionCountAbove && currentList.size > action.removeIfActionCountAbove ? newList : newList.push(action), List()).map(action => makeFlagAction({
        callback: () => {
          navigateToProductUrl({
            url: action.url,
            isRelativeUrl: !!action.relative
          });
        },
        displayText: action.displayText,
        id: "".concat(appIdentifier, "-").concat(action.displayText.replace(/\s/g, '-')),
        key: action.key
      }));
      dispatch(removeFlag(NO_SIMULTANEOUS_INSTALLS_FLAG_KEY));
      dispatch(removeFlag(loadingFlagKey));
      if (!suppressResultFlag) {
        dispatch(addFlag({
          flag: makeFlag({
            key,
            autoDismiss: false,
            appearance: SUCCESS_APPEARANCE,
            title: 'Success',
            description: makeFlagDescription({
              primary: "".concat(installableApp.details.name, " was added.")
            }),
            actions
          })
        }));
      }
      dispatch(finishInstallApp({
        context,
        installableAppId,
        suppressResultFlag,
        featureFlags,
        appDetails: installableApp.marketplaceAppDetails
      }));
      updateInstalledAppsPostInstall(dispatch, context, hostConfig);
      updateAllRequestStatusesPostInstall({
        hostInfo,
        appKey: installableApp.details.key,
        appName: installableApp.details.name
      });
      AppInstallTask.succeeded();
      return Either.right(installedAddon);
    }, error => {
      log.error('InstallApp', 'Failed to install the app', {
        error
      });
      trackAppInstallTelemetryTaskError(error);
      const key = "".concat(appIdentifier, "-app-install-error");
      if (!suppressResultFlag) {
        dispatch(addCaasErrorFlag({
          featureFlags,
          hostConfig,
          message: error.message,
          key,
          actions: List([makeFlagAction({
            callback: () => {
              dispatch(doInstallApp({
                context,
                hostConfig,
                hostInfo,
                mpacBaseUrl,
                installableApp,
                installableAppId,
                relayState,
                suppressProgressFlag,
                suppressResultFlag,
                featureFlags,
                locale
              }));
              dispatch(removeFlag(key));
            },
            displayText: 'Try again',
            id: "".concat(appIdentifier, "-try-again")
          })])
        }));
      }
      dispatch(removeFlag(NO_SIMULTANEOUS_INSTALLS_FLAG_KEY));
      dispatch(removeFlag(loadingFlagKey));
      dispatch(installAppError({
        context,
        error,
        installableAppId
      }));
      return Either.left(error);
    });
  };
};